import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Button, Chip, IconButton, Snackbar, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { useZeny } from './ZenyProvider';
import { enabled as desktopAlertsEnabled, notify } from './desktopAlerts';
import brand from '../shell/brand';
import { alertText, desktopText } from './alertText';

// Being told, as opposed to going to look.
//
// This is the whole of the notification: a line that appears when something worth stopping for is
// raised, and a way to the one place those are dealt with. It deliberately does not carry a list of
// its own - the exceptions page is that list, and a second copy in a panel only invited the question
// of why the two did not match.
//
// It is mounted by the shell rather than by any page, so it reaches an operator wherever they are
// working - gate, yard, map, reports. When they are not looking at Quadrant at all, three things
// carry the alert across: the browser's own notification if this desk has turned that on, the tab
// title, and the toast itself, which does not start counting down until the page is back in front.
export default function InAppNotifications({ onReview }) {
  const { items, updatedAt, error } = useZeny();
  const account = useSelector((state) => state.session.user);
  const seen = useRef(null);
  const title = useRef(typeof document === 'undefined' ? '' : document.title);
  const review = useRef(onReview);
  review.current = onReview;
  const [toast, setToast] = useState(null);
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  const [waiting, setWaiting] = useState(0);

  useEffect(() => {
    const change = () => {
      const away = document.hidden;
      setHidden(away);
      // Coming back is looking: the tab title stops carrying the count, and the toast - which has
      // been held open without a timer - starts its ten seconds now that somebody can read it.
      if (!away) setWaiting(0);
    };
    document.addEventListener('visibilitychange', change);
    return () => document.removeEventListener('visibilitychange', change);
  }, []);

  useEffect(() => {
    if (!updatedAt || error) return;
    const ids = new Set(items.map((item) => item.id));
    if (seen.current) {
      // A plant raises exceptions all day - documents expiring in a fortnight, a tag read out of
      // order. Those belong in the count, not in front of somebody mid-intake. Only what a desk
      // would stop for interrupts.
      const fresh = items.filter((item) => !seen.current.has(item.id) && item.status === 'RAISED' && ['CRITICAL', 'HIGH'].includes(item.severity));
      if (fresh.length) {
        // The lorry, then the rule, then how many others - worked out in one place so the toast and
        // the browser notification cannot end up saying different things about the same batch.
        setToast(alertText(fresh));
        if (document.hidden) {
          setWaiting((count) => count + fresh.length);
          if (desktopAlertsEnabled(account)) {
            notify({
              ...desktopText(fresh, brand.name),
              onClick: () => review.current && review.current(),
            });
          }
        }
      }
    }
    // Keep delivered IDs until sign-out, including ones removed from a later response.
    seen.current = new Set([...(seen.current || []), ...ids]);
  }, [items, updatedAt, error, account]);

  // A background tab is still a screen somebody glances at, so the count goes where they would see
  // it without switching: the title in the tab strip.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.title = waiting ? `(${waiting}) ${title.current}` : title.current;
    return () => { document.title = title.current; };
  }, [waiting]);

  return (
    <Snackbar
      open={!!toast}
      // Held open while nobody is there to read it, and while a critical one is unanswered.
      autoHideDuration={hidden || toast?.severity === 'CRITICAL' ? null : 10000}
      // A snackbar is dismissed by any click on the page unless it is told otherwise, which would
      // mean the next keystroke of whatever the operator was typing took the alert away before they
      // had read it. It goes when it times out, or when they close it.
      onClose={(event, reason) => { if (reason !== 'clickaway') setToast(null); }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        severity={toast?.severity === 'CRITICAL' ? 'error' : 'warning'}
        // An alert carries its own close control only until something is put in its action slot, so
        // the way out is given explicitly here. A critical alert never times out, and one that can
        // only be left by opening the exceptions page is a thing stuck to the screen.
        action={(
          <Stack direction="row" alignItems="center">
            <Button size="small" color="inherit" onClick={() => { setToast(null); onReview(); }}>Review</Button>
            <IconButton size="small" color="inherit" aria-label="Dismiss alert" onClick={() => setToast(null)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        )}
      >
        {toast && (
          <>
            <AlertTitle sx={{ mb: toast.detail ? 0.25 : 0, fontWeight: 700 }}>
              {toast.headline}
            </AlertTitle>
            {toast.detail && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>{toast.detail}</Typography>
            )}
            {toast.severity === 'CRITICAL' && (
              <Chip size="small" color="error" label="Critical" sx={{ mt: 0.75 }} />
            )}
          </>
        )}
      </Alert>
    </Snackbar>
  );
}
